import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useLocation } from "react-router-dom";
import { Send, X, Sparkles } from "lucide-react";

const EMOTIONS = {
  wave:  "/keen-wave.png",   // greeting / idle collapsed button
  think: "/keen-think.png",  // busy / waiting on AI reply
  cheer: "/keen-cheer.png",  // just delivered a reply (1.5s flash)
  work:  "/keen-work.png",   // chat open, awaiting user input
};

export default function MentorWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [cheering, setCheering] = useState(false);
  const location = useLocation();
  const scrollRef = useRef(null);

  // Pick avatar based on state
  const emotion = !open
    ? "wave"
    : busy
      ? "think"
      : cheering
        ? "cheer"
        : "work";
  const avatarSrc = EMOTIONS[emotion];

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "G'day! I'm Keen, your S0rted mentor. Ask me anything about money, jobs, renting, study, health — or just say hi.",
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/mentor/chat", {
        message: text,
        session_id: sessionId,
        context_page: location.pathname,
      });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", text: data.reply }]);
      // Briefly switch to "cheer" emotion after delivering a reply
      setCheering(true);
      setTimeout(() => setCheering(false), 1500);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", text: "Hmm, I had trouble responding. Try again in a moment?" }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="mentor-open-button"
          className="fixed bottom-20 right-6 md:bottom-6 md:right-44 z-50 group flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-neonpink text-white shadow-glowstrong animate-pulseglow"
        >
          <img
            src={avatarSrc}
            alt="Keen"
            className="w-12 h-12 rounded-full bg-ink object-cover animate-float-gentle"
          />
          <span className="font-display font-bold pr-1">Ask Keen</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="mentor-panel"
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[80vh] glass-card-strong flex flex-col overflow-hidden border border-white/10"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-neonpink/30 via-transparent to-neoncyan/20">
            <img
              src={avatarSrc}
              alt={`Keen (${emotion})`}
              data-testid={`mentor-avatar-${emotion}`}
              className="w-10 h-10 rounded-full bg-ink object-cover transition-all duration-300"
            />
            <div className="flex-1">
              <div className="font-display font-bold text-base">Keen</div>
              <div className="text-xs text-neongreen flex items-center gap-1"><Sparkles size={10}/> AI Mentor · Always here</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="mentor-close-button"
              className="p-2 rounded-full hover:bg-white/10"><X size={16}/></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`mentor-msg-${m.role}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-neonpink text-white rounded-br-sm"
                    : "bg-white/8 border border-white/10 text-white/95 rounded-bl-sm"
                }`}>{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-1 items-center text-white/60 text-sm">
                <div className="w-2 h-2 rounded-full bg-neoncyan animate-pulse"/>
                <div className="w-2 h-2 rounded-full bg-neoncyan animate-pulse" style={{animationDelay:'.2s'}}/>
                <div className="w-2 h-2 rounded-full bg-neoncyan animate-pulse" style={{animationDelay:'.4s'}}/>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-white/10 flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              data-testid="mentor-input"
              placeholder="Ask anything..."
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm placeholder:text-white/40 outline-none focus:border-neonpink"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="mentor-send-button"
              className="w-10 h-10 rounded-full bg-neonpink flex items-center justify-center disabled:opacity-50 hover:shadow-glowstrong transition">
              <Send size={16}/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
